import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, type InferType, ref } from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, Button, Input, PasswordInput } from "@envirohub/ui";
import { AlertCircle, X } from "lucide-react";
import { clearPasswordInputs } from "@envirohub/utils";
import { useRegisterOrganization } from "@/api";
import { useAuthError } from "@/hooks/useAuthError";

const schema = object({
  firstName: string().required("validation.firstNameMin").min(2, "validation.firstNameMin"),
  lastName: string().required("validation.lastNameMin").min(2, "validation.lastNameMin"),
  email: string().required("validation.emailRequired").email("validation.invalidEmail"),
  organizationName: string().required("validation.nameMin").min(3, "validation.nameMin"),
  legalName: string().default(""),
  crNumber: string().default(""),
  vatNumber: string().default(""),
  password: string()
    .required("validation.passwordRequired")
    .min(8, "validation.passwordRequirements")
    .matches(/[A-Z]/, "validation.passwordRequirements")
    .matches(/[a-z]/, "validation.passwordRequirements")
    .matches(/[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, "validation.passwordRequirements"),
  confirmPassword: string()
    .oneOf([ref("password")], "validation.passwordsMatch")
    .required("validation.passwordsMatch"),
});

type FormData = InferType<typeof schema>;

const formFields: ReadonlyArray<keyof FormData> = [
  "firstName",
  "lastName",
  "email",
  "organizationName",
  "legalName",
  "crNumber",
  "vatNumber",
  "password",
  "confirmPassword",
];

export default function RegisterOrganizationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resolveAuthError = useAuthError();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { legalName: "", crNumber: "", vatNumber: "" },
  });
  const registerOrganization = useRegisterOrganization();

  const passwordRequirementLabels = {
    length: t("validation.passwordLength"),
    upper: t("validation.passwordUpper"),
    lower: t("validation.passwordLower"),
    numberOrSpecial: t("validation.passwordNumberOrSpecial"),
  };

  const onSubmit = (data: FormData) => {
    setServerError(null);
    registerOrganization.mutate(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        organizationName: data.organizationName,
        ...(data.legalName?.trim() && { legalName: data.legalName.trim() }),
        ...(data.crNumber?.trim() && { crNumber: data.crNumber.trim() }),
        ...(data.vatNumber?.trim() && { vatNumber: data.vatNumber.trim() }),
      },
      {
        onSuccess: (result) => {
          navigate("/verify", { state: { contact: result.email, redirectTo: "/login" } });
        },
        onError: (error) => {
          const action = resolveAuthError(error);
          let mappedAny = false;
          Object.entries(action.fieldErrors).forEach(([key, message]) => {
            if ((formFields as string[]).includes(key)) {
              setError(key as keyof FormData, { message });
              mappedAny = true;
            }
          });
          if (!mappedAny) setServerError(action.message);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <h1 className="text-h3 font-bold text-surface-on dark:text-white">
          {t("auth.organizationRegisterTitle")}
        </h1>
        <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-1">
          {t("auth.organizationRegisterDescription")}
        </p>
      </div>

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {serverError && (
          <Alert variant="destructive" className="ps-10">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverError}</AlertDescription>
            <button
              type="button"
              onClick={() => setServerError(null)}
              className="absolute end-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
              aria-label={t("common.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </Alert>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            id="firstName"
            type="text"
            autoComplete="given-name"
            label={t("auth.firstName")}
            placeholder={t("auth.firstNamePlaceholder")}
            error={errors.firstName?.message ? t(errors.firstName.message) : undefined}
            required
            {...register("firstName")}
          />
          <Input
            id="lastName"
            type="text"
            autoComplete="family-name"
            label={t("auth.lastName")}
            placeholder={t("auth.lastNamePlaceholder")}
            error={errors.lastName?.message ? t(errors.lastName.message) : undefined}
            required
            {...register("lastName")}
          />
        </div>

        <Input
          id="email"
          type="email"
          autoComplete="username"
          label={t("auth.email")}
          placeholder={t("auth.emailPlaceholder")}
          error={errors.email?.message ? t(errors.email.message) : undefined}
          required
          {...register("email")}
        />

        <Input
          id="organizationName"
          type="text"
          autoComplete="organization"
          label={t("auth.organizationName")}
          placeholder={t("auth.companyNamePlaceholder")}
          error={errors.organizationName?.message ? t(errors.organizationName.message) : undefined}
          required
          {...register("organizationName")}
        />
        <Input
          id="legalName"
          type="text"
          label={t("auth.legalName")}
          error={errors.legalName?.message ? t(errors.legalName.message) : undefined}
          {...register("legalName")}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            id="crNumber"
            type="text"
            label={t("auth.crNumber")}
            error={errors.crNumber?.message ? t(errors.crNumber.message) : undefined}
            {...register("crNumber")}
          />
          <Input
            id="vatNumber"
            type="text"
            label={t("auth.vatNumber")}
            error={errors.vatNumber?.message ? t(errors.vatNumber.message) : undefined}
            {...register("vatNumber")}
          />
        </div>

        <PasswordInput
          id="password"
          autoComplete="new-password"
          label={t("auth.password")}
          placeholder={t("auth.passwordPlaceholder")}
          error={errors.password?.message ? t(errors.password.message) : undefined}
          showStrengthRequirements
          requirementLabels={passwordRequirementLabels}
          required
          {...register("password")}
        />
        <PasswordInput
          id="confirmPassword"
          autoComplete="new-password"
          label={t("auth.confirmPassword")}
          placeholder={t("auth.confirmPasswordPlaceholder")}
          error={errors.confirmPassword?.message ? t(errors.confirmPassword.message) : undefined}
          required
          {...register("confirmPassword")}
        />

        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={registerOrganization.isPending}
          className="w-full mt-4 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
        >
          {t("auth.register")}
        </Button>
      </form>

      <Link
        to="/register"
        onClick={() => clearPasswordInputs()}
        className="text-primary font-bold no-underline hover:underline text-center"
      >
        {t("auth.registerIndividual")}
      </Link>
      <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] text-center mt-2">
        {t("auth.alreadyHave")}{" "}
        <Link
          to="/login"
          onClick={() => clearPasswordInputs()}
          className="text-primary font-bold no-underline hover:underline"
        >
          {t("auth.login")}
        </Link>
      </p>
    </div>
  );
}
